import {
  InventoryCategoriesPage,
  InventoryDashboardPage,
  InventoryItemsPage,
  InventoryMovementHistoryReportPage,
  InventoryMovementSummaryReportPage,
  InventoryRecipesPage,
  InventoryReportsPage,
  InventoryUnitsPage,
  LowStockItemsPage,
  MenuItemsMissingRecipesPage,
  PurchaseInvoicesPage,
  ReturnToSupplierPage,
  StockAdjustmentsPage,
  StockBalancesPage,
  StockCountPage,
  StockLocationsPage,
  StockMovementsPage,
  StockTransfersPage,
  SupplierPaymentHistoryPage,
  SuppliersPage,
} from '@components/dashboard/inventory/InventoryPages';
import { AccessControlCode } from '@shared/constants';
import { Navigate, Route } from 'react-router-dom';
import { InventoryPage } from '../pages/InventoryPage';
import { AccessControlledDashboardPage } from './featureRouteHelpers';

export const createInventoryRoutes = () => (
  <Route path="/inventory" element={<InventoryPage />}>
    <Route index element={<Navigate to="/inventory/dashboard" replace />} />
    <Route
      path="dashboard"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.INVENTORY_PAGE_ACCESS}
          title="Inventory access restricted"
          description="Your account does not currently have permission to access inventory."
        >
          <InventoryDashboardPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route path="items">
      <Route index element={<Navigate to="/inventory/items/list" replace />} />
      <Route
        path="list"
        element={
          <AccessControlledDashboardPage
            control={AccessControlCode.INVENTORY_ITEMS_VIEW}
            title="Inventory items access restricted"
            description="Your account does not currently have permission to view inventory items."
          >
            <InventoryItemsPage />
          </AccessControlledDashboardPage>
        }
      />
      <Route
        path="categories"
        element={
          <AccessControlledDashboardPage
            control={AccessControlCode.INVENTORY_ITEMS_VIEW}
            title="Inventory categories access restricted"
            description="Your account does not currently have permission to manage inventory categories."
          >
            <InventoryCategoriesPage />
          </AccessControlledDashboardPage>
        }
      />
      <Route
        path="units"
        element={
          <AccessControlledDashboardPage
            control={AccessControlCode.INVENTORY_ITEMS_VIEW}
            title="Inventory units access restricted"
            description="Your account does not currently have permission to manage inventory units."
          >
            <InventoryUnitsPage />
          </AccessControlledDashboardPage>
        }
      />
    </Route>

    {/* Stock sub-section */}
    <Route path="stock">
      <Route
        index
        element={<Navigate to="/inventory/stock/locations" replace />}
      />
      <Route
        path="locations"
        element={
          <AccessControlledDashboardPage
            control={AccessControlCode.INVENTORY_STOCK_VIEW}
            title="Stock access restricted"
            description="Your account does not currently have permission to view stock locations."
          >
            <StockLocationsPage />
          </AccessControlledDashboardPage>
        }
      />
      <Route
        path="balances"
        element={
          <AccessControlledDashboardPage
            control={AccessControlCode.INVENTORY_STOCK_VIEW}
            title="Stock access restricted"
            description="Your account does not currently have permission to view stock balances."
          >
            <StockBalancesPage />
          </AccessControlledDashboardPage>
        }
      />
      <Route
        path="movements"
        element={
          <AccessControlledDashboardPage
            control={AccessControlCode.INVENTORY_STOCK_VIEW}
            title="Stock access restricted"
            description="Your account does not currently have permission to view stock movements."
          >
            <StockMovementsPage />
          </AccessControlledDashboardPage>
        }
      />
      <Route
        path="low-stock"
        element={
          <AccessControlledDashboardPage
            control={AccessControlCode.INVENTORY_STOCK_VIEW}
            title="Stock access restricted"
            description="Your account does not currently have permission to view low-stock items."
          >
            <LowStockItemsPage />
          </AccessControlledDashboardPage>
        }
      />
    </Route>

    <Route path="purchases">
      <Route
        index
        element={
          <AccessControlledDashboardPage
            control={AccessControlCode.INVENTORY_INVOICES_VIEW}
            title="Purchases access restricted"
            description="Your account does not currently have permission to view supplier invoices."
          >
            <PurchaseInvoicesPage />
          </AccessControlledDashboardPage>
        }
      />
      <Route
        path="payment-history"
        element={
          <AccessControlledDashboardPage
            control={AccessControlCode.INVENTORY_INVOICES_VIEW}
            title="Payment history access restricted"
            description="Your account does not currently have permission to view supplier payment history."
          >
            <SupplierPaymentHistoryPage />
          </AccessControlledDashboardPage>
        }
      />
    </Route>
    <Route
      path="suppliers"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.INVENTORY_SUPPLIERS_VIEW}
          title="Suppliers access restricted"
          description="Your account does not currently have permission to view suppliers."
        >
          <SuppliersPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="recipes"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.INVENTORY_RECIPES_VIEW}
          title="Recipes access restricted"
          description="Your account does not currently have permission to view recipes."
        >
          <InventoryRecipesPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="missing"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.INVENTORY_RECIPES_VIEW}
          title="Recipes access restricted"
          description="Your account does not currently have permission to view recipes."
        >
          <MenuItemsMissingRecipesPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="transfers"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.INVENTORY_STOCK_TRANSFER}
          title="Transfers access restricted"
          description="Your account does not currently have permission to manage stock transfers."
        >
          <StockTransfersPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="adjustments"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.INVENTORY_STOCK_ADJUST}
          title="Adjustments access restricted"
          description="Your account does not currently have permission to record stock adjustments."
        >
          <StockAdjustmentsPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="returns"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.INVENTORY_STOCK_ADJUST}
          title="Returns access restricted"
          description="Your account does not currently have permission to record supplier returns."
        >
          <ReturnToSupplierPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="stock-count"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.INVENTORY_STOCK_ADJUST}
          title="Stock count access restricted"
          description="Your account does not currently have permission to perform stock counts."
        >
          <StockCountPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="reports"
      element={<Navigate to="/inventory/reports/movement-history" replace />}
    />
    <Route
      path="reports/movement-history"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.INVENTORY_REPORTS_VIEW}
          title="Inventory reports access restricted"
          description="Your account does not currently have permission to view inventory movement history."
        >
          <InventoryMovementHistoryReportPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="reports/movement-summary"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.INVENTORY_REPORTS_VIEW}
          title="Inventory reports access restricted"
          description="Your account does not currently have permission to view inventory movement summary."
        >
          <InventoryMovementSummaryReportPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="reports/analytics"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.INVENTORY_REPORTS_VIEW}
          title="Inventory reports access restricted"
          description="Your account does not currently have permission to view inventory reports."
        >
          <InventoryReportsPage />
        </AccessControlledDashboardPage>
      }
    />
  </Route>
);
